import { isMobileDevice, isNative, isWeb, useBrowser } from "@stepapp/core";
import {
  Block,
  Button,
  Divider,
  ProfileInfo,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
} from "@stepapp/ui";
import { useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DimensionValue, Linking, Platform, ScrollView, StyleSheet } from "react-native";
import Animated, { interpolate, type SharedValue, useAnimatedStyle } from "react-native-reanimated";
import { closeDrawer } from "../../helpers/drawer";
import { useAppLinks, useAppSelector } from "../../hooks";
import { AppStorage } from "../../services/AppStorage";
import { useDisplayIntercom } from "../../services/Intercom";
import ShareCouponSheet from "../Modal/ShareCouponSheet";
import AppVersionInfo from "./AppVersionInfo";
import NavigationButton from "./NavigationButton";

const PARALLAX_ADDITION = 100;

const Drawer = ({ progress }: { progress: SharedValue<number> }) => {
  const { t } = useTranslation();
  const countryCode = AppStorage().get("countryCode");
  const isAuthenticated = !!AppStorage().get("token");
  const displayIntercom = useDisplayIntercom(countryCode);
  const { firstName, profile } = useAppSelector(({ user }) => user);
  const { push, navigate } = useRouter();
  const [shareCouponVisible, setShareCouponVisible] = useState(false);

  const { AboutUs, AppLanding, AppCustomer } = useAppLinks();

  const openAboutUs = useBrowser(AboutUs);

  const handleUserNavigation = useCallback(() => {
    closeDrawer();
    isAuthenticated ? navigate("/profile/") : navigate("/login/phone");
  }, [isAuthenticated, navigate]);

  const navigateTo = useCallback(
    (route: "payments" | "settings" | "coupons" | "support") => () => {
      closeDrawer();
      navigate(`/${route}`);
    },
    [navigate],
  );

  const handleBrowserOpen = useCallback(
    (callback: () => void) => () => {
      closeDrawer();
      callback();
    },
    [],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 0.1], [0, 1]),
    transform: [{ translateX: interpolate(progress.value, [0, 1], [-PARALLAX_ADDITION, 0]) }],
  }));

  return (
    <>
      <Animated.View style={[styles.container, animatedStyle]}>
        <Block style={styles.drawerContainer}>
          <ProfileInfo
            avatar={profile?.avatar}
            t={t}
            type="small"
            firstName={firstName}
            isProfileScreen={false}
            navigateToProfile={handleUserNavigation}
            marginTop={3}
            style={styles.drawerProfile}
          />

          <Block justifySpace flex={1}>
            <Block flex={1} paddingLeft={2.5}>
              <ScrollView>
                {isAuthenticated ? (
                  <>
                    <NavigationButton
                      icon="card"
                      label={t("components.drawer.payments")}
                      onPress={navigateTo("payments")}
                    />
                    <NavigationButton
                      icon="cog"
                      label={t("components.drawer.settings")}
                      onPress={navigateTo("settings")}
                    />
                    <NavigationButton
                      icon="coupon"
                      label={t("components.drawer.coupons")}
                      onPress={navigateTo("coupons")}
                      size={24}
                    />
                    <NavigationButton
                      icon="heart"
                      label={t("components.drawer.referAFriend")}
                      onPress={() => setShareCouponVisible(true)}
                    />
                    <NavigationButton
                      icon="envelope"
                      label={t("components.drawer.writeToUs")}
                      onPress={navigateTo("support")}
                    />
                    <NavigationButton
                      icon="chat-filled"
                      label={t("components.drawer.help")}
                      onPress={displayIntercom}
                    />
                    <NavigationButton
                      icon="offers-filled"
                      label={t("components.drawer.aboutStepapp")}
                      onPress={handleBrowserOpen(openAboutUs)}
                    />
                  </>
                ) : (
                  <>
                    <NavigationButton
                      icon="offers-filled"
                      label={t("components.drawer.aboutStepapp")}
                      onPress={handleBrowserOpen(openAboutUs)}
                    />
                    {isWeb && (
                      <NavigationButton
                        icon="offers-outline"
                        label={t("components.drawer.downloadApp")}
                        onPress={() => push(AppLanding)}
                      />
                    )}
                    <NavigationButton
                      icon="chat-filled"
                      label={t("components.drawer.help")}
                      onPress={displayIntercom}
                    />
                  </>
                )}
              </ScrollView>
            </Block>

            {isNative ? (
              <AppVersionInfo />
            ) : (
              <>
                <Divider spaceless />
                <Block justifyCenter padding={2.5}>
                  <Block alignCenter row marginBottom={2}>
                    <Block alignCenter justifyCenter marginRight={1.5} style={styles.appIcon}>
                      <Block style={styles.appIconBackground}>
                        <SvgIcon color={palette.primary.accent} name="offers-filled" size={40} />
                      </Block>
                    </Block>
                    <Typography variant="newH4">
                      {t("components.tabBar.stepappMobileApp")}
                    </Typography>
                  </Block>
                  <Block alignStart>
                    <Button
                      label={t("components.tabBar.openApp")}
                      onPress={() => Linking.openURL(isMobileDevice ? AppCustomer : AppLanding)}
                      type="secondary"
                      variant="small"
                    />
                  </Block>
                </Block>
              </>
            )}
          </Block>
        </Block>
        <Block style={styles.closeContainer}>
          <Touchable onPress={closeDrawer} style={styles.touchabeClose} />
        </Block>
      </Animated.View>

      <ShareCouponSheet toggle={() => setShareCouponVisible(false)} visible={shareCouponVisible} />
    </>
  );
};

export default React.memo(Drawer);

const styles = StyleSheet.create({
  appIcon: {
    backgroundColor: palette.primary.accent,
    borderRadius: 5,
    height: spacing.unit * 5,
    width: spacing.unit * 5,
  },
  appIconBackground: {
    backgroundColor: palette.primary.main,
  },
  closeContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    flexDirection: "row",
    height: Platform.select({ default: "100%", web: "100vh" as DimensionValue }),
    width: "100%",
  },
  drawerProfile: {
    paddingLeft: spacing.unit * 2.5,
  },
  drawerContainer: {
    backgroundColor: palette.common.white,
    paddingVertical: spacing.unit * 2,
    width: Math.min(spacing.deviceWidth * 0.75, 320),
  },
  touchabeClose: {
    backgroundColor: palette.common.transparent,
    flex: 1,
    width: "100%",
  },
});
